import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { Send, Plus, Trash2, Sparkles, MessageSquare } from "lucide-react";

const newMsgId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `m-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export default function Yunov() {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streaming, setStreaming] = useState("");
  const scrollRef = useRef(null);

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/yunov/conversations");
      setConversations(data);
    } catch {
      toast.error("Could not load conversations");
    }
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const newChat = () => {
    setActiveId(null);
    setMessages([]);
    setStreaming("");
  };

  const openConv = async (id) => {
    setActiveId(id);
    const { data } = await api.get(`/yunov/conversations/${id}`);
    setMessages((data.messages || []).map((m) => ({ ...m, id: m.id || newMsgId() })));
    setStreaming("");
  };

  const deleteConv = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    await api.delete(`/yunov/conversations/${id}`);
    if (activeId === id) newChat();
    loadConvs();
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    const userMsg = { id: newMsgId(), role: "user", content: text };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setStreaming("");

    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API}/yunov/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ conversation_id: activeId, message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error("Stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      let finalConv = activeId;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.delta) {
              acc += payload.delta;
              setStreaming(acc);
            }
            if (payload.done) {
              finalConv = payload.conversation_id;
            }
            if (payload.error) {
              toast.error(payload.error);
            }
          } catch {
            // Skip malformed SSE chunks (e.g. heartbeat lines); other lines continue
          }
        }
      }
      setMessages((m) => [...m, { id: newMsgId(), role: "assistant", content: acc }]);
      setStreaming("");
      if (finalConv && finalConv !== activeId) {
        setActiveId(finalConv);
      }
      loadConvs();
    } catch (err) {
      toast.error("Chat failed: " + (err?.message || ""));
      setStreaming("");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-screen md:h-screen">
      {/* Left: conv list */}
      <aside className="hidden lg:flex flex-col w-72 border-r border-black/5 bg-white">
        <div className="p-6 border-b border-black/5">
          <div className="uppercase-label">GCOMS.ai</div>
          <h2 className="font-heading text-lg font-semibold mt-1">Conversations</h2>
          <button
            data-testid="yunov-new-chat-button"
            onClick={newChat}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2 rounded-md bg-sage-deep text-white text-sm font-medium hover:bg-sage-deep/90"
          >
            <Plus size={16} /> New chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {conversations.length === 0 && (
            <div className="text-xs text-muted-foreground p-3">No conversations yet.</div>
          )}
          {conversations.map((c) => (
            <div
              key={c.id}
              onClick={() => openConv(c.id)}
              data-testid={`yunov-conv-${c.id}`}
              className={`group p-3 rounded-md cursor-pointer transition-colors mb-1 ${
                activeId === c.id ? "bg-bone" : "hover:bg-bone"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-medium truncate flex-1">{c.title || "Conversation"}</div>
                <button onClick={(e) => deleteConv(c.id, e)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive">
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{new Date(c.created_at).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      </aside>

      {/* Right: chat */}
      <section className="flex-1 flex flex-col bg-bone">
        <header className="px-6 lg:px-10 py-5 border-b border-black/5 bg-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-sage/15 text-sage-deep flex items-center justify-center">
            <Sparkles size={20} />
          </div>
          <div>
            <div className="font-heading text-lg font-semibold">GCOMS.ai</div>
            <div className="text-xs text-muted-foreground">Trained on Dr. Yunov's clinical reasoning</div>
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 lg:px-10 py-8">
          {messages.length === 0 && !streaming && (
            <div className="max-w-2xl mx-auto text-center py-20">
              <div className="w-14 h-14 rounded-full bg-sage/15 text-sage-deep mx-auto flex items-center justify-center">
                <MessageSquare size={26} />
              </div>
              <h3 className="font-heading text-2xl font-semibold mt-6">Ask any case question</h3>
              <p className="text-muted-foreground mt-3">
                Differential diagnoses, treatment sequencing, second-opinions on imaging, pharmacology choices — ask as if you were calling for a curbside.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mt-8 text-left">
                {[
                  "Best approach for a vertical root fracture in #19?",
                  "Antibiotic choice for an immunocompromised patient with periapical abscess?",
                  "When would you graft vs. place an immediate implant in #14?",
                  "Differential for chronic facial pain that doesn't follow a nerve distribution?",
                ].map((s) => (
                  <button
                    key={s}
                    data-testid="yunov-suggested-prompt"
                    onClick={() => setInput(s)}
                    className="text-sm text-left p-4 rounded-md border border-ink/10 bg-white hover:border-sage-deep transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="max-w-3xl mx-auto space-y-5">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-5 py-3 ${m.role === "user" ? "bubble-user" : "bubble-ai"}`}>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</div>
                </div>
              </div>
            ))}
            {streaming && (
              <div className="flex justify-start">
                <div className="max-w-[80%] px-5 py-3 bubble-ai">
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{streaming}<span className="animate-pulse">▋</span></div>
                </div>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={send} className="border-t border-black/5 bg-white px-6 lg:px-10 py-4">
          <div className="max-w-3xl mx-auto flex items-end gap-3">
            <textarea
              data-testid="yunov-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder="Ask about a case..."
              className="flex-1 resize-none px-4 py-3 rounded-md border border-ink/15 focus:outline-none focus:ring-2 focus:ring-sage/40 max-h-40"
              disabled={sending}
            />
            <button
              data-testid="yunov-send-button"
              type="submit"
              disabled={sending || !input.trim()}
              className="px-5 py-3 rounded-md bg-sage-deep text-white font-medium hover:bg-sage-deep/90 disabled:opacity-50 inline-flex items-center gap-2"
            >
              <Send size={16} /> Send
            </button>
          </div>
          <div className="max-w-3xl mx-auto text-xs text-muted-foreground mt-2">
            GCOMS.ai provides reference clinical reasoning. Use your judgment and verify before treatment.
          </div>
        </form>
      </section>
    </div>
  );
}
